/**
 * ChecklistDetail Component
 *
 * Detailed view of a single checklist with all items.
 * Allows completing items, adding notes, and photo evidence.
 *
 * @module pages/technician/checklists/ChecklistDetail
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationCircle,
  FaSpinner,
  FaTimes,
} from 'react-icons/fa';
import {
  getChecklist,
  completeChecklistItem,
  uncompleteChecklistItem,
  markItemNotApplicable,
  skipChecklistItem,
  completeChecklist,
  updateChecklistItem,
  uploadChecklistItemPhoto,
} from '../../../services/checklistService';
import { CHECKLIST_ITEM_STATUS } from '../../../utils/constants';
import ChecklistItem from './ChecklistItem';
import './ChecklistView.css';

const ChecklistDetail = () => {
  const { checklistId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [activeItemForPhoto, setActiveItemForPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [toast, setToast] = useState(null);

  // Load checklist data
  const loadChecklist = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await getChecklist(checklistId);

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Checklist no encontrado');

      // Sort items by sort_order
      const sortedItems = (data.items || []).sort((a, b) =>
        (a.sort_order || 0) - (b.sort_order || 0)
      );

      setChecklist({ ...data, items: sortedItems });
    } catch (err) {
      console.error('[ChecklistDetail] Load error:', err);
      setError(err.message || 'Error al cargar el checklist');
    } finally {
      setLoading(false);
    }
  }, [checklistId]);

  useEffect(() => {
    if (checklistId) {
      loadChecklist();
    }
  }, [checklistId, loadChecklist]);

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Handle item completion
  //
  // No GPS capture here: `project_checklist_items` has no latitude/longitude
  // columns and `completeChecklistItem` never read the `location` key it was
  // being handed. It was a geolocation permission prompt on every single tap
  // whose result went nowhere.
  const handleCompleteItem = async (itemId, data = {}) => {
    try {
      setSubmitting(true);

      const { data: updatedItem, checklistCompleted, error: completeError } =
        await completeChecklistItem(itemId, {
          notes: data.notes,
        });

      if (completeError) throw completeError;

      // Update local state
      setChecklist((prev) => {
        const updatedItems = prev.items.map((item) =>
          item.id === itemId ? { ...item, ...updatedItem } : item
        );
        const completedCount = updatedItems.filter(
          (i) =>
            i.status === CHECKLIST_ITEM_STATUS.COMPLETED ||
            i.status === CHECKLIST_ITEM_STATUS.NOT_APPLICABLE
        ).length;
        const progress = Math.round((completedCount / updatedItems.length) * 100);

        return {
          ...prev,
          items: updatedItems,
          completedItems: completedCount,
          progress,
          is_completed: checklistCompleted,
        };
      });

      if (checklistCompleted) {
        showToast('Checklist completado', 'success');
      } else {
        showToast('Item completado', 'success');
      }
    } catch (err) {
      console.error('[ChecklistDetail] Complete item error:', err);
      showToast(err.message || 'Error al completar item', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle item uncomplete
  const handleUncompleteItem = async (itemId) => {
    try {
      setSubmitting(true);

      const { data: updatedItem, error: uncompleteError } =
        await uncompleteChecklistItem(itemId);

      if (uncompleteError) throw uncompleteError;

      // Update local state
      setChecklist((prev) => {
        const updatedItems = prev.items.map((item) =>
          item.id === itemId
            ? { ...item, ...updatedItem, completed_by_profile: null }
            : item
        );
        const completedCount = updatedItems.filter(
          (i) =>
            i.status === CHECKLIST_ITEM_STATUS.COMPLETED ||
            i.status === CHECKLIST_ITEM_STATUS.NOT_APPLICABLE
        ).length;
        const progress = Math.round((completedCount / updatedItems.length) * 100);

        return {
          ...prev,
          items: updatedItems,
          completedItems: completedCount,
          progress,
          is_completed: false,
        };
      });

      showToast('Item desmarcado', 'info');
    } catch (err) {
      console.error('[ChecklistDetail] Uncomplete item error:', err);
      showToast(err.message || 'Error al desmarcar item', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle mark as N/A
  const handleMarkNA = async (itemId) => {
    try {
      setSubmitting(true);

      // No custom reason: the service stamps the `No aplica` sentinel that
      // `deriveItemStatus` reads back. Passing free text here used to bury it.
      const { data: updatedItem, error: naError } = await markItemNotApplicable(itemId);

      if (naError) throw naError;

      // Update local state
      setChecklist((prev) => {
        const updatedItems = prev.items.map((item) =>
          item.id === itemId ? { ...item, ...updatedItem } : item
        );
        const completedCount = updatedItems.filter(
          (i) =>
            i.status === CHECKLIST_ITEM_STATUS.COMPLETED ||
            i.status === CHECKLIST_ITEM_STATUS.NOT_APPLICABLE
        ).length;
        const progress = Math.round((completedCount / updatedItems.length) * 100);

        return {
          ...prev,
          items: updatedItems,
          completedItems: completedCount,
          progress,
        };
      });

      showToast('Marcado como N/A', 'info');
    } catch (err) {
      console.error('[ChecklistDetail] Mark N/A error:', err);
      showToast(err.message || 'Error al marcar N/A', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle skip item
  const handleSkipItem = async (itemId) => {
    try {
      setSubmitting(true);

      const { data: updatedItem, error: skipError } = await skipChecklistItem(itemId);

      if (skipError) throw skipError;

      // Update local state
      setChecklist((prev) => {
        const updatedItems = prev.items.map((item) =>
          item.id === itemId ? { ...item, ...updatedItem } : item
        );

        return {
          ...prev,
          items: updatedItems,
        };
      });

      showToast('Item omitido', 'warning');
    } catch (err) {
      console.error('[ChecklistDetail] Skip item error:', err);
      showToast(err.message || 'Error al omitir item', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle add photo trigger
  const handleAddPhotoTrigger = (itemId) => {
    setActiveItemForPhoto(itemId);
    fileInputRef.current?.click();
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeItemForPhoto) return;

    try {
      setUploadingPhoto(true);

      const { url, error: uploadError } = await uploadChecklistItemPhoto(
        file,
        checklistId,
        activeItemForPhoto
      );

      if (uploadError) throw uploadError;

      // Complete item with photo
      await handleCompleteItem(activeItemForPhoto, { photo_url: url });

      // Update item in local state
      setChecklist((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.id === activeItemForPhoto ? { ...item, photo_url: url } : item
        ),
      }));

      showToast('Foto agregada', 'success');
    } catch (err) {
      console.error('[ChecklistDetail] Photo upload error:', err);
      showToast(err.message || 'Error al subir foto', 'error');
    } finally {
      setUploadingPhoto(false);
      setActiveItemForPhoto(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle add note
  //
  // This used to be a pure `showToast('Nota guardada')` with no service call at
  // all: the technician typed a note, got the green confirmation, and nothing
  // was ever written. `project_checklist_items.notes` is a real column — write
  // to it, and only claim success once the write came back clean.
  const handleAddNote = async (itemId, note) => {
    try {
      setSubmitting(true);

      const { data: updatedItem, error: noteError } = await updateChecklistItem(itemId, {
        notes: note?.trim() ? note.trim() : null,
      });

      if (noteError) throw noteError;

      setChecklist((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.id === itemId ? { ...item, ...updatedItem } : item
        ),
      }));

      showToast('Nota guardada', 'success');
    } catch (err) {
      console.error('[ChecklistDetail] Save note error:', err);
      showToast(err.message || 'Error al guardar la nota', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle complete checklist manually
  const handleCompleteChecklist = async () => {
    try {
      setSubmitting(true);

      // `completeChecklist` takes ONE argument. It used to be called with a
      // second `completionNotes` string that went straight into the void:
      // `project_checklists` has no notes column at all (verified against
      // information_schema), so the "Notas de finalizacion" textarea was
      // collecting text nothing could ever store. The textarea is gone; if the
      // business wants those notes, that needs a migration (see the report),
      // not a silently-dropped argument.
      const { error: completeError } = await completeChecklist(checklistId);

      if (completeError) throw completeError;

      setShowCompleteModal(false);
      showToast('Checklist finalizado', 'success');

      // Reload checklist
      await loadChecklist();
    } catch (err) {
      console.error('[ChecklistDetail] Complete checklist error:', err);
      showToast(err.message || 'Error al finalizar checklist', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Check that nothing is still pending.
  //
  // This used to filter on `item.is_required`, a column that does not exist on
  // `project_checklist_items` (only the checklist itself has `is_required`), so
  // the list was always empty and `.every()` on an empty array is always true —
  // the gate never gated anything.
  const canCompleteChecklist = () => {
    if (!checklist?.items?.length) return false;
    return checklist.items.every((item) => item.status !== CHECKLIST_ITEM_STATUS.PENDING);
  };

  // Get checklist-type label. The column is `checklist_type`; there is no
  // `category` column on `project_checklists`.
  const getChecklistTypeLabel = (type) => {
    const labels = {
      pre_work: 'Pre-trabajo',
      safety: 'Seguridad',
      quality: 'Calidad',
      completion: 'Cierre',
      delivery: 'Entrega',
      custom: 'Personalizado',
      general: 'General',
    };
    return labels[type] || type || 'General';
  };

  // Loading state
  if (loading) {
    return (
      <div className="checklist-detail-loading">
        <FaSpinner className="spinner" />
        <p>Cargando checklist...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="checklist-detail-error">
        <FaExclamationCircle className="error-icon" />
        <p>{error}</p>
        <button className="retry-btn" onClick={loadChecklist}>
          Reintentar
        </button>
        <button className="back-btn" onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>
    );
  }

  if (!checklist) {
    return null;
  }

  const pendingItems = checklist.items.filter(
    (i) => i.status === CHECKLIST_ITEM_STATUS.PENDING
  );
  const completedItems = checklist.items.filter(
    (i) =>
      i.status === CHECKLIST_ITEM_STATUS.COMPLETED ||
      i.status === CHECKLIST_ITEM_STATUS.NOT_APPLICABLE
  );
  const skippedItems = checklist.items.filter(
    (i) => i.status === CHECKLIST_ITEM_STATUS.SKIPPED
  );

  return (
    <div className="checklist-detail">
      {/* Header */}
      <div className="checklist-detail-header">
        <button
          className="back-button"
          onClick={() => navigate(-1)}
          aria-label="Volver"
        >
          <FaArrowLeft />
        </button>
        <div className="header-info">
          <h1 className="checklist-title">{checklist.title}</h1>
          <span className="checklist-category">
            {getChecklistTypeLabel(checklist.checklist_type)}
          </span>
        </div>
        {checklist.is_completed && (
          <div className="completed-badge">
            <FaCheckCircle /> Completado
          </div>
        )}
      </div>

      {/* Progress bar */}
      <div className="checklist-progress-section">
        <div className="progress-info">
          <span className="progress-text">
            {checklist.completedItems} de {checklist.totalItems} completados
          </span>
          <span className="progress-percentage">{checklist.progress}%</span>
        </div>
        <div className="progress-bar-container">
          <div
            className="progress-bar-fill"
            style={{ width: `${checklist.progress}%` }}
          />
        </div>
      </div>

      {/* Description */}
      {checklist.description && (
        <div className="checklist-description">{checklist.description}</div>
      )}

      {/* Items list */}
      <div className="checklist-items-container">
        {/* Pending items */}
        {pendingItems.length > 0 && (
          <div className="items-section">
            <h2 className="section-title">Pendientes ({pendingItems.length})</h2>
            <div className="items-list">
              {pendingItems.map((item) => (
                <ChecklistItem
                  key={item.id}
                  item={item}
                  onComplete={handleCompleteItem}
                  onUncomplete={handleUncompleteItem}
                  onAddPhoto={handleAddPhotoTrigger}
                  onAddNote={handleAddNote}
                  onMarkNA={handleMarkNA}
                  onSkip={handleSkipItem}
                  disabled={submitting || checklist.is_completed}
                />
              ))}
            </div>
          </div>
        )}

        {/* Completed items */}
        {completedItems.length > 0 && (
          <div className="items-section">
            <h2 className="section-title completed">
              Completados ({completedItems.length})
            </h2>
            <div className="items-list">
              {completedItems.map((item) => (
                <ChecklistItem
                  key={item.id}
                  item={item}
                  onComplete={handleCompleteItem}
                  onUncomplete={handleUncompleteItem}
                  onAddPhoto={handleAddPhotoTrigger}
                  onAddNote={handleAddNote}
                  disabled={submitting || checklist.is_completed}
                />
              ))}
            </div>
          </div>
        )}

        {/* Skipped items */}
        {skippedItems.length > 0 && (
          <div className="items-section">
            <h2 className="section-title skipped">
              Omitidos ({skippedItems.length})
            </h2>
            <div className="items-list">
              {skippedItems.map((item) => (
                <ChecklistItem
                  key={item.id}
                  item={item}
                  disabled={true}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Complete button */}
      {!checklist.is_completed && checklist.progress === 100 && (
        <div className="complete-checklist-section">
          <button
            className="complete-checklist-btn"
            onClick={() => setShowCompleteModal(true)}
            disabled={submitting}
          >
            <FaCheckCircle /> Finalizar Checklist
          </button>
        </div>
      )}

      {/* Complete modal */}
      {showCompleteModal && (
        <div className="modal-overlay" onClick={() => setShowCompleteModal(false)}>
          <div className="complete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Finalizar Checklist</h2>
              <button
                className="modal-close"
                onClick={() => setShowCompleteModal(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Estas a punto de finalizar el checklist <strong>{checklist.title}</strong>.
              </p>
              <p className="warning-text">
                Esta accion no se puede deshacer.
              </p>
              {/* No "Notas de finalizacion" field: `project_checklists` has no
                  column to store them, so the textarea was collecting text that
                  was dropped on the floor. Per-item notes DO persist. */}
            </div>
            <div className="modal-footer">
              <button
                className="cancel-btn"
                onClick={() => setShowCompleteModal(false)}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                className="confirm-btn"
                onClick={handleCompleteChecklist}
                disabled={submitting || !canCompleteChecklist()}
              >
                {submitting ? (
                  <>
                    <FaSpinner className="spinner" /> Finalizando...
                  </>
                ) : (
                  <>
                    <FaCheckCircle /> Confirmar
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden file input for photos */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handlePhotoUpload}
        style={{ display: 'none' }}
      />

      {/* Photo upload loading overlay */}
      {uploadingPhoto && (
        <div className="upload-overlay">
          <div className="upload-modal">
            <FaSpinner className="spinner" />
            <p>Subiendo foto...</p>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ChecklistDetail;
